import { from } from 'rxjs';
import { reportReader$ } from './reportReader$';
import type { ReportReader$State, ReportSearchResults$State } from './types';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { isEmpty, isEqual, pick } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { drilldownWasTriggered } from './actions';
import { ApiResponse, reportApi } from '@ardoq/api';
import {
  APIReportFilters,
  APISearchResponse,
  ArdoqId,
  GraphlakeSnapshotId,
} from '@ardoq/api-types';
import type { Maybe } from '@ardoq/common-helpers';
import {
  ReportPagination,
  ReportSort,
  toSortKeyArg,
} from '@ardoq/report-reader';
import { keepValidReaderStateFilters } from './utils';
import { mapSearchResults } from './mapSearchResults';

const statePropertiesThatShouldTriggerSearchResultsToExecute: Array<
  keyof ReportReader$State
> = [
  'sort',
  'pagination',
  'reportId',
  'slideId',
  'presentationId',
  'filters',
  'selectedSnapshot',
  'hasFinishedFetchReport',
  'columns',
  'refreshTimestamp',
  'lastUpdated',
  'reportAPIError',
];

export const pickStateThatShouldTriggerSearchResultsToExecute = (
  state: ReportReader$State
): ReportReader$State =>
  pick(state, statePropertiesThatShouldTriggerSearchResultsToExecute);

const toApiRequest = (
  state: ReportSearchResults$State
): FetchSearchResultParams => ({
  reportId: state.reportId!,
  filters: state.filters,
  sort: {
    ...state.sort,
    columnKey: toSortKeyArg(state.columns, state.sort.columnKey),
  },
  pagination: state.pagination,
  // If refreshTimestamp is set, prevent getting cached response by sending "no-cache" to the server
  noCache: Number.isInteger(state.refreshTimestamp),
  snapshot: state.selectedSnapshot,
});

export const fetchSearchResults = async ({
  reportId,
  sort,
  pagination,
  filters,
  noCache,
  snapshot,
}: FetchSearchResultParams): ApiResponse<APISearchResponse> => {
  dispatchAction(drilldownWasTriggered(true));
  const searchResults = await reportApi.drilldown(reportId, {
    from: pagination.from,
    size: pagination.size,
    sort: sort.columnKey ?? undefined,
    order: sort.order,
    filters: isEmpty(filters) ? undefined : filters,
    noCache,
    snapshot: snapshot ?? undefined,
  });
  dispatchAction(drilldownWasTriggered(false));
  return searchResults;
};

export type FetchSearchResultParams = {
  reportId: ArdoqId;
  noCache?: boolean;
  sort: ReportSort;
  pagination: ReportPagination;
  filters: Maybe<APIReportFilters>;
  snapshot: GraphlakeSnapshotId | null;
};

export type FetchSearchResults = (
  searchResults$state: FetchSearchResultParams
) => ApiResponse<APISearchResponse>;

export const searchResults$ = reportReader$.pipe(
  // NOTE: type inference breaks when there's more than 10 operators,
  // that's why this map performs more than one operation
  map(data =>
    pickStateThatShouldTriggerSearchResultsToExecute(
      keepValidReaderStateFilters(data)
    )
  ),
  distinctUntilChanged<ReportSearchResults$State>(isEqual),
  filter(
    (
      a: ReportSearchResults$State
    ): a is ReportSearchResults$State & {
      reportId: ArdoqId;
    } => Boolean(a.reportId && a.hasFinishedFetchReport && !a.reportAPIError)
  ),
  map(toApiRequest),
  debounceTime(300),
  switchMap(request =>
    from(fetchSearchResults(request)).pipe(
      map(response =>
        mapSearchResults({
          searchResults: response,
          snapshot: request.snapshot,
          pagination: request.pagination,
        })
      )
    )
  ),
  startWith({
    apiError: null,
    searchResults: null,
    filterableColumns: [],
    filters: null,
  })
);
